/** Deterministic settlement growth derived from persistent accomplishments
 * and the same recognized room geometry that licenses production. */
import type { HomesteadRoom } from "../homestead/rooms.js";
import {
  cloneSettlementState,
  type SettlementStage,
  type SettlementStateV1,
} from "./production.js";

export interface SettlementStageDef {
  stage: SettlementStage;
  name: string;
  populationCap: number;
}

export const SETTLEMENT_STAGES: Readonly<Record<SettlementStage, SettlementStageDef>> = {
  0: { stage: 0, name: "Camp", populationCap: 2 },
  1: { stage: 1, name: "Homestead", populationCap: 5 },
  2: { stage: 2, name: "Hamlet", populationCap: 8 },
  // Launch ceiling locked 2026-08-13: the ladder tops out at 10 (§14).
  3: { stage: 3, name: "Township", populationCap: 10 },
};

export type SettlementGrowthRequirementId =
  "residents" | "dwellings" | "facilities" | "commons" | "commissions";

export interface SettlementGrowthRequirement {
  id: SettlementGrowthRequirementId;
  label: string;
  current: number;
  required: number;
  met: boolean;
}

export interface SettlementGrowthStatus {
  stage: SettlementStage;
  name: string;
  populationCap: number;
  appeal: number;
  nextStage: SettlementStage | null;
  nextName: string | null;
  /** Only Camp → Homestead is playable in this slice. */
  nextAvailable: boolean;
  ready: boolean;
  requirements: SettlementGrowthRequirement[];
}

export interface SettlementGrowthResult {
  ok: boolean;
  state: SettlementStateV1;
  err?: string;
}

export function settlementPopulationCap(stage: SettlementStage): number {
  return SETTLEMENT_STAGES[stage].populationCap;
}

/** Appeal is explanatory rather than spendable: useful furnishing variety,
 * recognized purposes, real rooms, and established stage all contribute.
 * Duplicate furniture cannot inflate it because room comfort is categorical. */
export function settlementAppeal(rooms: readonly HomesteadRoom[], stage: SettlementStage): number {
  const functions = new Set(rooms.flatMap((room) => room.functions));
  const comfort = rooms.reduce((sum, room) => sum + room.comfort, 0);
  return Math.min(999, rooms.length * 2 + functions.size * 2 + comfort + stage * 4);
}

function activeFacilityCount(state: SettlementStateV1, rooms: readonly HomesteadRoom[]): number {
  return state.facilities.filter((facility) => rooms.some((room) =>
    room.functionAnchors[facility.roomFunction] === facility.anchorPid
      && room.functionSlots[facility.roomFunction].length > 0)).length;
}

function requirement(id: SettlementGrowthRequirementId, label: string,
  current: number, required: number): SettlementGrowthRequirement {
  return { id, label, current, required, met: current >= required };
}

export function settlementGrowthStatus(state: SettlementStateV1,
  rooms: readonly HomesteadRoom[]): SettlementGrowthStatus {
  const current = SETTLEMENT_STAGES[state.stage];
  const nextStage = state.stage < 3 ? (state.stage + 1) as SettlementStage : null;
  const requirements = state.stage === 0 ? [
    requirement("residents", "Residents", state.residents.length, 2),
    requirement("dwellings", "Distinct dwellings",
      rooms.filter((room) => room.functionSlots.dwelling.length > 0).length, 2),
    requirement("facilities", "Active facilities", activeFacilityCount(state, rooms), 2),
    requirement("commons", "Commons", rooms.filter((room) => room.functions.includes("commons")).length, 1),
    requirement("commissions", "Lifetime commissions", state.completedCommissions, 3),
  ] : [];
  const nextAvailable = state.stage === 0;
  return {
    stage: state.stage,
    name: current.name,
    populationCap: current.populationCap,
    appeal: settlementAppeal(rooms, state.stage),
    nextStage,
    nextName: nextStage === null ? null : SETTLEMENT_STAGES[nextStage].name,
    nextAvailable,
    ready: nextAvailable && requirements.every((entry) => entry.met),
    requirements,
  };
}

/** Pure explicit advancement. Callers settle production first, then persist
 * this result transactionally with the authoritative room solution. */
export function advanceSettlementStage(input: SettlementStateV1,
  rooms: readonly HomesteadRoom[], nowMs: number): SettlementGrowthResult {
  const state = cloneSettlementState(input);
  const status = settlementGrowthStatus(state, rooms);
  if (!status.nextAvailable) {
    return { ok: false, state, err: "NEXT SETTLEMENT STAGE IS NOT AVAILABLE YET" };
  }
  if (!status.ready) return { ok: false, state, err: "SETTLEMENT STAGE REQUIREMENTS NOT MET" };
  state.stage = status.nextStage!;
  state.updatedAt = Number.isFinite(nowMs) ? Math.max(0, Math.floor(nowMs)) : state.updatedAt;
  return { ok: true, state };
}
